import { useMemo, useRef } from "react";
import type { StudentQuestionAnswer, StudentTestExercise } from "p6m-p6u";
import useBreakpoints, { Breakpoint } from "../../../hooks/useBreakpoints";
import ExerciseWrapper from "./ExerciseWrapper";
import useExerciseAnswers from "../../../hooks/useExerciseAnswers";
import WordBox from "../../../basic/wordBox/WordBox";
import UnderlinedInput from "../../../basic/underlinedInput/UnderlinedInput";
import { focusNextOrBlur, getExerciseProgress, parseGapSentence } from "../../../helpers/OnlineTestHelper";

interface GapSentenceExerciseProps {
    exercise: StudentTestExercise;
    exerciseIndex: number;
    studentAnswer: StudentQuestionAnswer;
    onAnswerUpdate: (updatedAnswer: StudentQuestionAnswer, exerciseId: string) => void;
}

const GapSentenceExercise = (props: GapSentenceExerciseProps) => {
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const { exerciseId, answers, updateItemAtPosition } = useExerciseAnswers({
        exercise: props.exercise,
        studentAnswer: props.studentAnswer,
        onAnswerUpdate: props.onAnswerUpdate,
    });
    const progress = getExerciseProgress(props.studentAnswer, props.exercise.questionMode);

    const inputRefs = useRef<(HTMLTextAreaElement | null)[]>([]);
    const inputRefCallbacks = useRef<((el: HTMLTextAreaElement | null) => void)[]>([]);

    const getInputRef = (index: number) => {
        if (!inputRefCallbacks.current[index]) {
            inputRefCallbacks.current[index] = (el) => {
                inputRefs.current[index] = el;
            };
        }
        return inputRefCallbacks.current[index];
    };

    const fillWords = useMemo(
        () => props.studentAnswer.fillInTheGapWords ?? [],
        [props.studentAnswer.fillInTheGapWords]
    );

    const handleEnter = (index: number) => focusNextOrBlur(inputRefs.current, index);

    const renderGapSentences = () => {
        if (!answers.length) return null;

        return (
            <div className={`flex flex-col ${getStyleBasedOnBreakpoint(Breakpoint.MD, "", "gap-s")}`}>
                {answers.map((item, index) => {
                    if (!item.gap_sentence) return null;

                    const parts = parseGapSentence(item.gap_sentence);

                    return (
                        <div
                            key={item.id ?? index}
                            className={`bg-white flex flex-wrap items-center gap-x-s gap-y-xs ${getStyleBasedOnBreakpoint(
                                Breakpoint.MD,
                                "p-s",
                                "px-m py-s border border-p6-grey-light rounded-medium"
                            )}`}
                        >
                            {parts.map((part, partIndex) => (
                                <span
                                    key={`part-${partIndex}`}
                                    className="contents"
                                >
                                    {part && <span className="text-m text-p6-grey-dark">{part}</span>}
                                    {partIndex < parts.length - 1 && (
                                        <span className="inline-flex items-end w-[200px]">
                                            <UnderlinedInput
                                                data-testid={`online-test-gap-input-${index}`}
                                                inputRef={getInputRef(index)}
                                                text={item.studentAnswer ?? ""}
                                                setText={(value) => updateItemAtPosition(index, value)}
                                                maxLines={1}
                                                onEnter={() => handleEnter(index)}
                                            />
                                        </span>
                                    )}
                                </span>
                            ))}
                        </div>
                    );
                })}
            </div>
        );
    };

    if (!exerciseId) return null;

    return (
        <ExerciseWrapper
            data-testid="online-test-exercise-gap-sentence"
            number={props.exerciseIndex + 1}
            title={props.exercise.extraComments ?? ""}
            progress={progress}
        >
            <div className="flex flex-col gap-s">
                <WordBox words={fillWords} />
                {renderGapSentences()}
            </div>
        </ExerciseWrapper>
    );
};

export default GapSentenceExercise;
